/**
 * cart-bridge.ts
 *
 * Platform-aware Direct-to-Cart bridge for the forge-tryon widget.
 *
 * Supports auto-detection of nopCommerce, Shopify, and WooCommerce. Falls
 * back to a generic JSON POST when a `cartUrl` is provided, or returns an
 * error result when no cart endpoint is configured.
 *
 * All requests are same-origin (the Web Component script runs on the host
 * store domain), so CORS is not a concern and credentials can be included.
 */

/** Supported e-commerce platforms (plus sentinel values). */
export type CartPlatform =
  | 'nopcommerce'
  | 'shopify'
  | 'woocommerce'
  | 'custom'
  | 'none';

/** Options passed when constructing the bridge. */
export interface CartBridgeOptions {
  /** Fallback cart endpoint URL (used for 'custom' platform). */
  cartUrl?: string;
  /** Explicit platform override — skips auto-detection when set. */
  platform?: CartPlatform;
  /**
   * Optional mapping from Mirrably product IDs to the host store's product IDs.
   * When a mapping exists for the requested productId, the mapped value is used
   * instead for the platform cart request.
   */
  productIdMap?: Record<string, string>;
}

/** Input to a single add-to-cart request. */
export interface CartAddRequest {
  /** Mirrably product identifier. */
  productId: string;
  /** Platform variant/SKU identifier (e.g. Shopify variant ID). */
  variantId?: string;
  /** Number of units to add. Defaults to 1. */
  quantity?: number;
  /**
   * nopCommerce only: a single product-attribute name/value pair.
   * When the product has no required attributes, omit this field and the
   * bridge sends a bare quantity POST (works for attribute-free products).
   *
   * Full attribute mapping (multi-attribute products) requires catalog
   * metadata from the host store and is out of scope for this bridge.
   */
  variantAttribute?: { name: string; value: string };
  /**
   * Additional product attributes passed by the widget (e.g. `{ size: "M" }`).
   *
   * How each platform uses this:
   *  - nopCommerce: each entry is appended to the form body as a named field.
   *    The nopCommerce plugin maps attribute names to numeric product-attribute IDs
   *    via an admin-configured mapping table.
   *    TODO: implement admin attribute-name → ID mapping in the nopCommerce plugin
   *    (Sprint 3 scope). For now, fields are passed by name and the plugin should
   *    resolve them. Example: `product_attribute_<id>=<value>` where ID is looked
   *    up from a future `MirrablyAttributeMap` store setting.
   *  - Shopify: appended to the cart item `properties` object.
   *  - WooCommerce: appended to the POST body as extra fields (handled by plugin).
   *  - custom: merged into the JSON body under `attributes`.
   */
  attributes?: Record<string, string>;
}

/** Result returned by `addToCart`. */
export interface CartAddResult {
  /** Whether the cart operation succeeded. */
  ok: boolean;
  /** The platform that processed (or attempted) the request. */
  platform: CartPlatform;
  /** HTTP status code from the platform endpoint, when available. */
  status?: number;
  /** Human-readable message — error detail on failure, confirmation on success. */
  message?: string;
}

// ---------------------------------------------------------------------------
// Platform detection
// ---------------------------------------------------------------------------

/**
 * Inspect the host document to determine which e-commerce platform is running.
 *
 * Detection priority (first match wins):
 *  1. nopCommerce — AntiForgery token in a product form, or `window.nopAjaxCart`
 *  2. Shopify — `window.Shopify` or checkout API meta tag
 *  3. WooCommerce — body class or `form.cart [name="add-to-cart"]`
 *  4. 'none' — no known platform signals detected
 *
 * @param doc - Document to inspect. Defaults to the global `document`.
 */
export function detectPlatform(doc: Document = document): CartPlatform {
  // nopCommerce: product details form with AntiForgery token, or nopAjaxCart global
  const nopProductForm = doc.querySelector('form#product-details-form');
  const nopAntiforgery = doc.querySelector(
    'form#product-details-form input[name="__RequestVerificationToken"]',
  );
  // Loose nopCommerce signal: any form with the AntiForgery input (older themes)
  const nopToken = doc.querySelector('input[name="__RequestVerificationToken"]');
  const nopAjaxCart =
    typeof (globalThis as Record<string, unknown>)['nopAjaxCart'] !== 'undefined';

  if (nopProductForm || nopAntiforgery || nopAjaxCart || nopToken) {
    return 'nopcommerce';
  }

  // Shopify: global Shopify object or checkout API meta tag
  const shopifyGlobal =
    typeof (globalThis as Record<string, unknown>)['Shopify'] !== 'undefined';
  const shopifyMeta = doc.querySelector('meta[name="shopify-checkout-api-token"]');

  if (shopifyGlobal || shopifyMeta) {
    return 'shopify';
  }

  // WooCommerce: body class or form.cart with add-to-cart input
  const wooBody = doc.body?.classList.contains('woocommerce') ?? false;
  const wooForm =
    doc.querySelector('form.cart') !== null &&
    doc.querySelector('form.cart [name="add-to-cart"]') !== null;

  if (wooBody || wooForm) {
    return 'woocommerce';
  }

  return 'none';
}

// ---------------------------------------------------------------------------
// Per-platform POST implementations
// ---------------------------------------------------------------------------

const TIMEOUT_MS = 5_000;

/** Create an AbortController that fires after TIMEOUT_MS. */
function makeTimeoutController(): { controller: AbortController; clear: () => void } {
  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), TIMEOUT_MS);
  return { controller, clear: () => clearTimeout(id) };
}

/** Wrap a fetch error into a CartAddResult. */
function fetchError(platform: CartPlatform, err: unknown): CartAddResult {
  // DOMException does not always extend Error in all runtimes (jsdom, WebKit).
  // Check `name` on the raw value rather than going through `instanceof Error`.
  const errName = (err as { name?: string } | null)?.name ?? '';
  const errMsg =
    err instanceof Error ? err.message : err instanceof Object ? String(err) : String(err);
  const isAbort = errName === 'AbortError' || errMsg.toLowerCase().includes('abort');
  const message = isAbort ? 'Request timed out after 5 s' : errMsg;
  return { ok: false, platform, message };
}

/** POST to nopCommerce `/addproducttocart/details/{productId}/1` */
async function addToCartNopCommerce(
  productId: string,
  req: CartAddRequest,
  doc: Document,
): Promise<CartAddResult> {
  const qty = req.quantity ?? 1;
  const url = `/addproducttocart/details/${productId}/1`;

  const params = new URLSearchParams();
  params.set('quantity', String(qty));

  // Antiforgery token — required by nopCommerce CSRF middleware
  const tokenInput = doc.querySelector<HTMLInputElement>(
    'form#product-details-form input[name="__RequestVerificationToken"]',
  ) ?? doc.querySelector<HTMLInputElement>('input[name="__RequestVerificationToken"]');

  if (tokenInput?.value) {
    params.set('__RequestVerificationToken', tokenInput.value);
  }

  // Optional single variant attribute (legacy API)
  if (req.variantAttribute) {
    params.set(req.variantAttribute.name, req.variantAttribute.value);
  }

  // Additional attributes (e.g. size from the size selector).
  // Passed by name; the nopCommerce plugin resolves name → product_attribute_<id>
  // via a future admin mapping table (TODO Sprint 3).
  if (req.attributes) {
    for (const [name, value] of Object.entries(req.attributes)) {
      params.set(name, value);
    }
  }

  const { controller, clear } = makeTimeoutController();
  try {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: params.toString(),
      credentials: 'same-origin',
      signal: controller.signal,
    });
    clear();

    // nopCommerce returns JSON { success: bool, message: string, ... }
    let body: { success?: boolean; message?: string } = {};
    try {
      body = (await res.json()) as typeof body;
    } catch {
      // Non-JSON body — treat HTTP 2xx as success
    }

    if (res.ok && body.success !== false) {
      const result: CartAddResult = { ok: true, platform: 'nopcommerce', status: res.status };
      if (body.message) result.message = body.message;
      return result;
    }
    return {
      ok: false,
      platform: 'nopcommerce',
      status: res.status,
      message: body.message ?? `HTTP ${res.status}`,
    };
  } catch (err) {
    clear();
    return fetchError('nopcommerce', err);
  }
}

/** POST to Shopify `/cart/add.js` */
async function addToCartShopify(
  productId: string,
  req: CartAddRequest,
): Promise<CartAddResult> {
  const { controller, clear } = makeTimeoutController();
  try {
    const body: Record<string, unknown> = {
      id: req.variantId ?? productId,
      quantity: req.quantity ?? 1,
    };
    // Shopify stores custom item properties under `properties`
    if (req.attributes && Object.keys(req.attributes).length > 0) {
      body['properties'] = req.attributes;
    }
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify(body),
      credentials: 'same-origin',
      signal: controller.signal,
    });
    clear();

    if (res.ok) {
      return { ok: true, platform: 'shopify', status: res.status };
    }
    let message = `HTTP ${res.status}`;
    try {
      const body = (await res.json()) as { description?: string; message?: string };
      message = body.description ?? body.message ?? message;
    } catch { /* ignore parse errors */ }
    return { ok: false, platform: 'shopify', status: res.status, message };
  } catch (err) {
    clear();
    return fetchError('shopify', err);
  }
}

/** POST to WooCommerce `/?wc-ajax=add_to_cart` */
async function addToCartWooCommerce(
  productId: string,
  req: CartAddRequest,
  doc: Document,
): Promise<CartAddResult> {
  const params = new URLSearchParams();
  params.set('product_id', productId);
  params.set('quantity', String(req.quantity ?? 1));

  if (req.variantId) {
    params.set('variation_id', req.variantId);
  }

  // WooCommerce nonce — located in the hidden `woocommerce-cart-nonce` input
  const nonceInput = doc.querySelector<HTMLInputElement>('input[name="woocommerce-cart-nonce"]');
  if (nonceInput?.value) {
    params.set('security', nonceInput.value);
  }

  // Additional attributes (e.g. size from the size selector)
  if (req.attributes) {
    for (const [name, value] of Object.entries(req.attributes)) {
      params.set(name, value);
    }
  }

  const { controller, clear } = makeTimeoutController();
  try {
    const res = await fetch('/?wc-ajax=add_to_cart', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: params.toString(),
      credentials: 'same-origin',
      signal: controller.signal,
    });
    clear();

    if (res.ok) {
      return { ok: true, platform: 'woocommerce', status: res.status };
    }
    let message = `HTTP ${res.status}`;
    try {
      const body = (await res.json()) as { error?: string; message?: string };
      message = body.error ?? body.message ?? message;
    } catch { /* ignore parse errors */ }
    return { ok: false, platform: 'woocommerce', status: res.status, message };
  } catch (err) {
    clear();
    return fetchError('woocommerce', err);
  }
}

/** Generic JSON POST to a custom `cartUrl`. */
async function addToCartCustom(
  cartUrl: string,
  productId: string,
  req: CartAddRequest,
): Promise<CartAddResult> {
  const { controller, clear } = makeTimeoutController();
  try {
    const body: Record<string, unknown> = {
      productId,
      variantId: req.variantId,
      quantity: req.quantity ?? 1,
    };
    if (req.attributes && Object.keys(req.attributes).length > 0) {
      body['attributes'] = req.attributes;
    }
    const res = await fetch(cartUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      credentials: 'same-origin',
      signal: controller.signal,
    });
    clear();

    if (res.ok) {
      return { ok: true, platform: 'custom', status: res.status };
    }
    return { ok: false, platform: 'custom', status: res.status, message: `HTTP ${res.status}` };
  } catch (err) {
    clear();
    return fetchError('custom', err);
  }
}

// ---------------------------------------------------------------------------
// Main entry point
// ---------------------------------------------------------------------------

/**
 * Add the requested item to the host store's shopping cart.
 *
 * Platform is resolved in this order:
 *  1. `opts.platform` (explicit override via `cart-platform` attribute)
 *  2. `detectPlatform()` (DOM-based auto-detection)
 *  3. Fall back to 'custom' when `opts.cartUrl` is provided
 *  4. Return `{ ok: false, platform: 'none' }` when nothing is configured
 *
 * The `opts.productIdMap` can translate Mirrably product IDs to the host
 * store's internal IDs before any platform request is made.
 *
 * @param req  - Cart item details.
 * @param opts - Bridge configuration (cartUrl, platform override, id map).
 * @param doc  - Document used for DOM detection/token reading. Defaults to `document`.
 */
export async function addToCart(
  req: CartAddRequest,
  opts: CartBridgeOptions,
  doc: Document = document,
): Promise<CartAddResult> {
  // Resolve the host-platform product ID (fall through to req.productId when no mapping)
  const hostProductId = opts.productIdMap?.[req.productId] ?? req.productId;

  // Determine which platform to use
  let platform: CartPlatform;
  if (opts.platform && opts.platform !== 'none') {
    platform = opts.platform;
  } else {
    const detected = detectPlatform(doc);
    if (detected !== 'none') {
      platform = detected;
    } else if (opts.cartUrl) {
      platform = 'custom';
    } else {
      return { ok: false, platform: 'none', message: 'No cart endpoint configured' };
    }
  }

  switch (platform) {
    case 'nopcommerce':
      return addToCartNopCommerce(hostProductId, req, doc);

    case 'shopify':
      return addToCartShopify(hostProductId, req);

    case 'woocommerce':
      return addToCartWooCommerce(hostProductId, req, doc);

    case 'custom':
      if (!opts.cartUrl) {
        return {
          ok: false,
          platform: 'custom',
          message: 'cart-url is required for custom platform',
        };
      }
      return addToCartCustom(opts.cartUrl, hostProductId, req);

    default:
      return { ok: false, platform: 'none', message: 'No cart endpoint configured' };
  }
}
